import {
  AdminLearningContentRow,
  AdminLearningContentSubCategory,
  AdminLearningContentTemplate,
} from '../types/domain';
import { buildLearningContentScopeVersion } from './learningContentCacheVersion';
import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import { deviceCacheStorage } from './deviceCacheStorage';

/** v3: contentVersion(head) 비교용 메타 저장 */
const ROWS_CACHE_VERSION = 'v3';
const TEMPLATES_CACHE_VERSION = 'v3';

function rowsStorageKey(
  workspaceId: string,
  gradeId: string,
  gradeNumber: number,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): string {
  const t = templateName.trim();
  return `cp.workspaceLearningContent.rows.${ROWS_CACHE_VERSION}.${workspaceId}.${gradeId}.${gradeNumber}.${subCategory}.${encodeURIComponent(t)}`;
}

function templatesStorageKey(
  workspaceId: string,
  gradeId: string,
  gradeNumber: number,
  subCategory: AdminLearningContentSubCategory
): string {
  return `cp.workspaceLearningContent.templates.${TEMPLATES_CACHE_VERSION}.${workspaceId}.${gradeId}.${gradeNumber}.${subCategory}`;
}

function normalizeRows(raw: unknown): AdminLearningContentRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((row): row is AdminLearningContentRow => !!row && typeof row === 'object');
}

function normalizeTemplates(raw: unknown): AdminLearningContentTemplate[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((row): row is AdminLearningContentTemplate => !!row && typeof row === 'object');
}

export function loadWorkspaceLearningContentRowsCache(
  workspaceId: string,
  gradeId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): { found: boolean; rows: AdminLearningContentRow[]; contentVersion: string | null } {
  if (!workspaceId || !gradeId || !templateName.trim() || typeof window === 'undefined') {
    return { found: false, rows: [], contentVersion: null };
  }
  try {
    const raw = deviceCacheStorage.getItem(
      rowsStorageKey(workspaceId, gradeId, gradeNumber, subCategory, templateName)
    );
    if (!raw) return { found: false, rows: [], contentVersion: null };
    const parsed = JSON.parse(raw) as
      | { rows?: unknown; savedAt?: number; contentVersion?: string }
      | AdminLearningContentRow[];
    if (Array.isArray(parsed)) {
      const rows = normalizeRows(parsed);
      return { found: true, rows, contentVersion: buildLearningContentScopeVersion(rows) };
    }
    if (!parsed || typeof parsed !== 'object') return { found: false, rows: [], contentVersion: null };
    const rows = normalizeRows((parsed as { rows?: unknown }).rows);
    const stored = typeof (parsed as { contentVersion?: unknown }).contentVersion === 'string'
      ? (parsed as { contentVersion: string }).contentVersion.trim()
      : '';
    return {
      found: true,
      rows,
      contentVersion: stored || buildLearningContentScopeVersion(rows),
    };
  } catch {
    return { found: false, rows: [], contentVersion: null };
  }
}

export function saveWorkspaceLearningContentRowsCache(
  workspaceId: string,
  gradeId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templateName: string,
  rows: AdminLearningContentRow[],
  options?: { force?: boolean; contentVersion?: string }
): void {
  if (!workspaceId || !gradeId || !templateName.trim() || typeof window === 'undefined') {
    return;
  }
  if (!options?.force && !canWriteSchoolScopedDataCache(workspaceId)) {
    return;
  }
  try {
    const contentVersion =
      (options?.contentVersion ?? '').trim() || buildLearningContentScopeVersion(rows);
    const payload = { rows, contentVersion, savedAt: Date.now() };
    deviceCacheStorage.setItem(
      rowsStorageKey(workspaceId, gradeId, gradeNumber, subCategory, templateName),
      JSON.stringify(payload)
    );
  } catch {
    /* quota */
  }
}

export function clearWorkspaceLearningContentRowsCache(
  workspaceId: string,
  gradeId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templateName: string
): void {
  if (!workspaceId || !gradeId || !templateName.trim() || typeof window === 'undefined') return;
  try {
    deviceCacheStorage.removeItem(
      rowsStorageKey(workspaceId, gradeId, gradeNumber, subCategory, templateName)
    );
  } catch {
    /* ignore */
  }
}

/** 편제명 변경 시 LS 캐시 키·행 template_name을 함께 이전한다. */
export function migrateWorkspaceLearningContentRowsCache(
  workspaceId: string,
  gradeId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  fromTemplateName: string,
  toTemplateName: string,
  updatedAt: string
): void {
  const fromName = fromTemplateName.trim();
  const toName = toTemplateName.trim();
  if (!workspaceId || !gradeId || !fromName || !toName || fromName === toName) return;
  const cached = loadWorkspaceLearningContentRowsCache(
    workspaceId,
    gradeId,
    gradeNumber,
    subCategory,
    fromName
  );
  if (!cached.found) {
    clearWorkspaceLearningContentRowsCache(workspaceId, gradeId, gradeNumber, subCategory, fromName);
    return;
  }
  const migrated = cached.rows.map((row) => ({
    ...row,
    template_name: toName,
    updated_at: updatedAt,
  }));
  saveWorkspaceLearningContentRowsCache(
    workspaceId,
    gradeId,
    gradeNumber,
    subCategory,
    toName,
    migrated,
    { force: true }
  );
  clearWorkspaceLearningContentRowsCache(workspaceId, gradeId, gradeNumber, subCategory, fromName);
}

export function loadWorkspaceLearningContentTemplatesCache(
  workspaceId: string,
  gradeId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory
): { found: boolean; templates: AdminLearningContentTemplate[]; contentVersion: string | null } {
  if (!workspaceId || !gradeId || typeof window === 'undefined') {
    return { found: false, templates: [], contentVersion: null };
  }
  try {
    const raw = deviceCacheStorage.getItem(
      templatesStorageKey(workspaceId, gradeId, gradeNumber, subCategory)
    );
    if (!raw) return { found: false, templates: [], contentVersion: null };
    const parsed = JSON.parse(raw) as
      | { templates?: unknown; savedAt?: number; contentVersion?: string }
      | AdminLearningContentTemplate[];
    if (Array.isArray(parsed)) {
      const templates = normalizeTemplates(parsed);
      return {
        found: true,
        templates,
        contentVersion: buildLearningContentScopeVersion(templates),
      };
    }
    if (!parsed || typeof parsed !== 'object') {
      return { found: false, templates: [], contentVersion: null };
    }
    const templates = normalizeTemplates((parsed as { templates?: unknown }).templates);
    const stored = typeof (parsed as { contentVersion?: unknown }).contentVersion === 'string'
      ? (parsed as { contentVersion: string }).contentVersion.trim()
      : '';
    return {
      found: true,
      templates,
      contentVersion: stored || buildLearningContentScopeVersion(templates),
    };
  } catch {
    return { found: false, templates: [], contentVersion: null };
  }
}

export function saveWorkspaceLearningContentTemplatesCache(
  workspaceId: string,
  gradeId: string,
  gradeNumber: 1 | 2 | 3 | 4 | 5 | 6,
  subCategory: AdminLearningContentSubCategory,
  templates: AdminLearningContentTemplate[],
  options?: { force?: boolean; contentVersion?: string }
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  if (!options?.force && !canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    const contentVersion =
      (options?.contentVersion ?? '').trim() || buildLearningContentScopeVersion(templates);
    const payload = { templates, contentVersion, savedAt: Date.now() };
    deviceCacheStorage.setItem(
      templatesStorageKey(workspaceId, gradeId, gradeNumber, subCategory),
      JSON.stringify(payload)
    );
  } catch {
    /* quota */
  }
}
